import './Transactions.scss';

import { Column } from "primereact/column";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from "../../component/datatable2";

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ActionMenu from '../../component/actionmenu/ActionMenu';
import { MessageContext } from "../../context/MessageContext";
import { AccessLevel, useAuth } from '../../hook/useAuth';
import { TransactionService } from '../../service/TransactionService';
import { Constants } from '../../utils/Constants';
import { FormattedUtils } from '../../utils/FormattedUtils';
import useNavigationContext from '../../utils/navigationcontext/NavigationContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import ActionButton from '../../component/actionbutton/ActionButton';
import { ReactComponent as TransactionDetailsIcon } from '../../assets/images/transaction-details.svg';
import TransactionDetailsDialog from './TransactionDetailsDialog';
import { LocationService } from '../../service/LocationService';
import { DeviceService } from '../../service/DeviceService';
import { ProductService } from '../../service/ProductService';
import { MerchantService } from '../../service/MerchantService';
import PeriodSelector from '../../component/periodselector/PeriodSelector';

const Transactions = () => {
    return (
        <DataTableProvider>
            <TransactionsImpl />
        </DataTableProvider>
    );
};
const TransactionsImpl = () => {

    const { t } = useTranslation();
    const tableRef = useRef();
    const [statusOptions, setStatusOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);

    const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
    const [operatorOptions, setOperatorOptions] = useState([]);
    const [merchantOptions, setMerchantOptions] = useState([]);
    const [transactionDetailsDialogState, setTransactionDetailsDialogState] = useState({ visible: false, transactionId: null });
    
    const { showErrorMessage } = useContext(MessageContext);
    const isMounted = useRef(false);
    const navigate = useNavigate();
    const { isUserInAnyRole, getAccessLevel } = useAuth();
    
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);

    const accessLevel = useMemo(() => getAccessLevel(), [getAccessLevel]);


    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 25,
        sortField: 'date',
        sortOrder: -1,
        baseQuery: '',
        filters: {},
        month: new Date(),
        period: {
            from: moment().startOf('day'),
            to: moment().startOf('day').add(1, 'day'),
            periodType: 'day'
        },
    }), []);

    const navigationContext = useNavigationContext((params  ) => {
        setSearchCriteria(params?.searchCriteria ? params.searchCriteria : defaultSearchCriteria);
    });

    useEffect(() => {
        navigationContext.putParam('searchCriteria', searchCriteria);
    }, [searchCriteria]);


    useEffect(() => {
        isMounted.current = true;
        fetchStatusOptions();
        fetchPaymentMethodOptions();
        fetchOperatorOptions();
        fetchMerchantOptions();
        if (accessLevel == AccessLevel.MERCHANT) {
            fetchLocationOptions();
            fetchDeviceOptions();
            fetchProductOptions();
        }        
        return () => {
            isMounted.current = false;
        }
            
    }, []);

    const fetchStatusOptions = () => {
        TransactionService.getStatusList()
            .then(statuses => {
                if (isMounted.current) {
                    setStatusOptions(statuses.map(r => ({id: r, label: t(`TransactionStatus.${r}`)})))
                }
            })
            .catch(err => {
                showErrorMessage(t('transactions.error.fetchStatuses'))
            })
    }

    const fetchLocationOptions = () => {
        LocationService.getAllLocations()
            .then(locations => {
                setLocationOptions(locations.map(l => ({ id: l.id, label: l.name })));
            })
            .catch(err => {
                showErrorMessage(t('transactions.error.fetchLocationOptions'))
            })
    }
    const fetchDeviceOptions = () => {
        DeviceService.getAllDevices()
            .then(devices => {
                setDeviceOptions(devices.map(d => ({ id: d.id, label: d.name })));
            })
            .catch(err => {
                showErrorMessage(t('transactions.error.fetchDeviceOptions'))
            })
    }
    const fetchProductOptions = () => {
        ProductService.getAllProducts()
            .then(products => {
                setProductOptions(products.map(p => ({ id: p.id, label: p.name })));
            })
            .catch(err => {
                showErrorMessage(t('transactions.error.fetchProductOptions'))
            })
    }
    const fetchPaymentMethodOptions = () => {
        TransactionService.getPaymentMethodList()
            .then(paymentMethods => {
                if (isMounted.current) {
                    setPaymentMethodOptions(paymentMethods.map(r => ({ id: r, label: t(`PaymentMethod.${r}`) })))
                }
            })
            .catch(err => {
                showErrorMessage(t('transactions.error.fetchPaymentMethodOptions'))
            })
    }

    const fetchOperatorOptions = () => {
        if (accessLevel === AccessLevel.ADMIN) {
            TransactionService.getOperatorList()
                .then(operators => {
                    setOperatorOptions(operators.map(o => ({ id: o.id, label: o.name })));
                })
                .catch(err => {
                    showErrorMessage(t('transactions.error.fetchOperatorOptions'))
                })
        }
    }

    const fetchMerchantOptions = () => {
        if (accessLevel === AccessLevel.ADMIN || accessLevel === AccessLevel.OPERATOR) {
            MerchantService.getSimpleList()
                .then(merchants => {
                    setMerchantOptions(merchants.map(m => ({id: m.id, label: m.name})));
                })
                .catch(err => {
                    showErrorMessage(t('transactions.error.fetchMerchantOptions'))
                })
        }
    }

    const apiValueFromConverter = useMemo(() => 
        value => moment(value).startOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET), 
        []
    );
    const apiValueToConverter = useMemo(() => 
        value => moment(value).endOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET), 
        []
    );
    const apiValueConverter = useMemo(() => 
        value => value.map(r => r.id).join(','), 
        []
    );

    const filterConfig = useMemo(() => [
            {
                fieldName: 'operator.name',
                label: t('transactions.operatorName'),
                type: FILTER_TYPES.LIST,
                params: (accessLevel === AccessLevel.ADMIN || accessLevel === AccessLevel.OPERATOR) ? operatorOptions : [],
                apiParamName: 'operators',
                apiValueConverter
            },
            {
                fieldName: 'merchant.name',
                label: t('transactions.merchantName'),
                type: FILTER_TYPES.LIST,
                params: (accessLevel === AccessLevel.ADMIN || accessLevel === AccessLevel.OPERATOR) ? merchantOptions : [],
                apiParamName: 'merchants',
                apiValueConverter
            },
            {
                fieldName: 'date',
                label: t('transactions.date'),
                type: FILTER_TYPES.DATE_RANGE,
                apiParamName: 'date',
                apiValueFromConverter,
                apiValueToConverter
            },
            {
                fieldName: 'location.name',
                label: t('transactions.locationName'),
                type: accessLevel === AccessLevel.MERCHANT ? FILTER_TYPES.LIST : FILTER_TYPES.TEXT,
                params: accessLevel === AccessLevel.MERCHANT ? locationOptions : null,
                apiParamName: accessLevel === AccessLevel.MERCHANT ? 'locations' : 'location',
                apiValueConverter: accessLevel === AccessLevel.MERCHANT ? apiValueConverter : null
            },
            {
                fieldName: 'device.name',
                label: t('transactions.deviceName'),
                type: accessLevel === AccessLevel.MERCHANT ? FILTER_TYPES.LIST : FILTER_TYPES.TEXT,
                params: accessLevel === AccessLevel.MERCHANT ? deviceOptions : null,
                apiParamName: accessLevel === AccessLevel.MERCHANT ? 'devices' : 'device',
                apiValueConverter: accessLevel === AccessLevel.MERCHANT ? apiValueConverter : null
            },
            {
                fieldName: 'terminal.tid',
                label: t('transactions.tid'),
                type: FILTER_TYPES.TEXT,
                apiParamName: 'tid',
            },
            {
                fieldName: 'mdbId',
                label: t('transactions.mdbId'),
                type: FILTER_TYPES.TEXT,
                apiParamName: 'mdb_id',
            },
            {
                fieldName: 'product.productName',
                label: t('transactions.products'),
                type: accessLevel === AccessLevel.MERCHANT ? FILTER_TYPES.LIST : FILTER_TYPES.TEXT,
                params: accessLevel === AccessLevel.MERCHANT ? productOptions : null,
                apiParamName: accessLevel === AccessLevel.MERCHANT ? 'products' : 'product',
                apiValueConverter: accessLevel === AccessLevel.MERCHANT ? apiValueConverter : null
            },
            {
                fieldName: 'totalGrossAmount',
                label: t('transactions.totalGrossAmount'),
                type: FILTER_TYPES.AMOUNT,
                apiParamName: 'total_gross_amount',
                params: {
                    maxValue: 9999, // opcjonalne
                    minValue: 0      // opcjonalne
                }
            },
            {
                fieldName: 'payment.paymentMethod',
                label: t('transactions.paymentMethod'),
                type: FILTER_TYPES.LIST,
                params: paymentMethodOptions,
                apiParamName: 'payment_methods',
                apiValueConverter:  value => value.map(r => r.id).join(',')
            },
            {
                fieldName: 'payment.cardNumber',
                label: t('transactions.cardNumber'),
                type: FILTER_TYPES.TEXT,
                apiParamName: 'card_number'
            },
            {
                fieldName: 'status',
                label: t('transactions.status'),
                type: FILTER_TYPES.LIST,
                params: statusOptions,
                apiParamName: 'statuses',
                apiValueConverter:  value => value.map(r => r.id).join(',')
            },
    ], [statusOptions, paymentMethodOptions, operatorOptions, merchantOptions, locationOptions, deviceOptions, productOptions, apiValueConverter, accessLevel]);

    const normalizeTransactionCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
        month: criteria?.month ?? defaultSearchCriteria.month,
        period: {
            ...(criteria?.period ?? defaultSearchCriteria.period),
            from: moment(criteria?.period?.from ?? defaultSearchCriteria.period.from).toISOString(),
            to: moment(criteria?.period?.to ?? defaultSearchCriteria.period.to).toISOString(),
            periodType: (criteria?.period?.periodType ?? defaultSearchCriteria.period.periodType),
        },
    });

    const fetchData = async (criteria) => {
        try {
            return await TransactionService.getList(normalizeTransactionCriteria(criteria));
        } catch (err) {
            console.log('TransactionService::getList err', err);
            showErrorMessage(t('transactions.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };

    const TransactionsPeriodToolbar = ({ defaultSearchCriteria }) => {
        const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);

        return (
            <PeriodSelector
                value={searchCriteria?.period ?? defaultSearchCriteria.period}
                onChange={(period) => {
                    if (period?.from && period?.to && period?.periodType) {
                        setSearchCriteria(prev => ({
                            ...prev,
                            first: 0,
                            timestamp: Date.now(),
                            period: {
                                ...period,
                                from: moment(period.from).toISOString(),
                                to: moment(period.to).toISOString(),
                                periodType: period.periodType,
                            },
                        }));
                    }
                }}
            />
        );
    };


    const tableToolbarRight = (
        <TransactionsPeriodToolbar defaultSearchCriteria={defaultSearchCriteria} />
    );

    const generateMenuItems = (row) => {
        const ctxParam = navigationContext.getContextParam();
        const items = [];
        if (isUserInAnyRole(['TRANSACTIONS_READ'])) { 
            items.push({label: t('transactions.menu.details'), icon: <TransactionDetailsIcon className="icon" />, 
                command: () => setTransactionDetailsDialogState({ visible: true, transactionId: row.id })
            });
        }
        return items;
    };

    const menuRef = useRef();

    return (
        <div className='data-card transactions-container'>
            <TransactionDetailsDialog visible={transactionDetailsDialogState.visible} transactionId={transactionDetailsDialogState.transactionId} 
                onHide={() => setTransactionDetailsDialogState({ visible: false, transactionId: null })} 
            />
            <ActionMenu ref={menuRef} />
            <DataTable
                ref={tableRef}
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={defaultSearchCriteria}
                tableToolbarRight={tableToolbarRight}
                minWidth="1120px"
            >
                {accessLevel === AccessLevel.ADMIN && (
                    <Column
                        field="partner.name"
                        header={t('transactions.operatorName')}
                        sortable
                        sortField="operator.name"
                        id="partnerName"
                        headerClassName="dt2-header-wrap"
                        style={{
                            minWidth: '90px',
                            maxWidth: '140px',
                        }}
                    />
                )}

                {(accessLevel === AccessLevel.ADMIN ||
                    accessLevel === AccessLevel.OPERATOR) && (
                    <Column
                        field="merchant.name"
                        header={t('transactions.merchantName')}
                        sortable
                        sortField="merchant.name"
                        id="merchantName"
                        headerClassName="dt2-header-wrap"
                        style={{
                            minWidth: '100px',
                            maxWidth: '150px',
                        }}
                    />
                )}

                <Column
                    field="date"
                    header={t('transactions.date')}
                    sortable
                    sortField="date"
                    id="date"
                    headerClassName="dt2-header-wrap"
                    body={row => RenderedHelper.formatTimestamp(row.date)}
                    style={{
                        minWidth: '105px',
                        maxWidth: '145px',
                    }}
                />

                <Column
                    field="locationName"
                    header={t('transactions.locationName')}
                    sortable
                    sortField="location.name"
                    id="locationName"
                    style={{
                        minWidth: '120px',
                        maxWidth: '150px',
                    }}
                />

                <Column
                    field="deviceName"
                    header={t('transactions.deviceName')}
                    sortable
                    sortField="device.name"
                    id="deviceName"
                    style={{
                        minWidth: '110px',
                        maxWidth: '150px',
                    }}
                />

                <Column
                    field="tid"
                    header={t('transactions.tid')}
                    sortable
                    sortField="terminal.tid"
                    id="tid"
                    style={{
                        minWidth: '80px',
                        maxWidth: '100px',
                    }}
                />

                <Column
                    field="mdbId"
                    header={t('transactions.mdbId')}
                    sortable
                    sortField="mdbId"
                    id="mdbId"
                    body={row => row.mdbId ?? ''}
                    style={{
                        minWidth: '30px',
                        maxWidth: '90px',
                    }}
                />

                <Column
                    field="productName"
                    header={t('transactions.products')}
                    sortable
                    sortField="product.productName"
                    id="productName"
                    style={{
                        minWidth: '100px',
                        maxWidth: '150px',
                    }}
                />

                <Column
                    field="totalGrossAmount"
                    header={t('transactions.totalGrossAmount')}
                    sortable
                    id="totalGrossAmount"
                    className="price-column"
                    headerClassName="dt2-header-wrap"
                    body={row =>
                        FormattedUtils.formatPrice(
                            row.totalGrossAmount,
                            row.currency,
                        )
                    }
                    style={{
                        minWidth: '70px',
                        maxWidth: '125px',
                    }}
                />

                <Column
                    field="paymentMethod"
                    header={t('transactions.paymentMethod')}
                    sortable
                    sortField="payment.paymentMethod"
                    id="paymentMethod"
                    headerClassName="dt2-header-wrap"
                    body={row =>
                        RenderedHelper.formatEnum(
                            row.paymentMethod,
                            'PaymentMethod',
                        )
                    }
                    style={{
                        minWidth: '120px',
                        maxWidth: '130px',
                    }}
                />

                <Column
                    field="cardNumber"
                    header={t('transactions.cardNumber')}
                    sortable
                    sortField="payment.cardNumber"
                    id="cardNumber"
                    headerClassName="dt2-header-wrap"
                    style={{
                        minWidth: '95px',
                        maxWidth: '130px',
                    }}
                />

                <Column
                    field="status"
                    header={t('transactions.status')}
                    sortable
                    id="status"
                    body={row =>
                        RenderedHelper.formatEnum(
                            row.status,
                            'TransactionStatus',
                        )
                    }
                    style={{
                        minWidth: '90px',
                        maxWidth: '125px',
                    }}
                />

                <Column
                    className="dt2-actions-column"
                    headerClassName="dt2-actions-column"
                    bodyClassName="dt2-actions-column"
                    style={{
                        width: '48px',
                        minWidth: '48px',
                        maxWidth: '48px',
                    }}
                    body={row => (
                        <ActionButton
                            menuRef={menuRef}
                            generateMenuItems={() =>
                                generateMenuItems(row)
                            }
                            showMainNavigation={false}
                        />
                    )}
                />
            </DataTable>
        </div>
    );
}

export default Transactions;